import React, { useState, useEffect } from 'react';
import {
    Calendar,
    Clock,
    User,
    Building,
    Mail,
    MoreVertical,
    Trash2,
    CheckCircle,
    XCircle,
    Search,
    Filter
} from 'lucide-react';
import { adminApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { format } from 'date-fns';
import { useModal } from '../ui/ModalProvider';

const ScheduleManagement = () => {
    const { showConfirm } = useModal();
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const { toast } = useToast();

    useEffect(() => {
        loadSchedules();
    }, []);

    const loadSchedules = async () => {
        try {
            setLoading(true);
            const response = await adminApi.getSchedules();
            setSchedules(response.data);
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to load schedules",
                variant: "destructive"
            });
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (id, status) => {
        try {
            await adminApi.updateSchedule(id, { status });
            setSchedules(schedules.map(s => s.id === id ? { ...s, status } : s));
            toast({
                title: "Success",
                description: `Appointment ${status} successfully`,
            });
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to update appointment",
                variant: "destructive"
            });
        }
    };

    const handleDelete = (id) => {
        showConfirm('Are you sure you want to delete this appointment?', async () => {
            try {
                await adminApi.deleteSchedule(id);
                setSchedules(schedules.filter(s => s.id !== id));
                toast({
                    title: "Deleted",
                    description: "Appointment removed from records",
                });
            } catch (error) {
                toast({
                    title: "Error",
                    description: "Failed to delete appointment",
                    variant: "destructive"
                });
            }
        });
    };

    const filteredSchedules = schedules.filter(s => {
        const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (s.company && s.company.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const getStatusBadge = (status) => {
        switch (status) {
            case 'confirmed': return 'bg-green-500/10 text-green-500 border border-green-500/20';
            case 'cancelled': return 'bg-red-500/10 text-red-500 border border-red-500/20';
            default: return 'bg-yellow-500/10 text-yellow-500 border border-yellow-500/20';
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-silver mb-2">Schedule Management</h1>
                    <p className="text-gray-400">Track and manage your upcoming discovery sessions</p>
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                        type="text"
                        placeholder="Search by name, email, or company..."
                        className="w-full bg-gray-900 border border-gray-800 rounded-lg pl-10 pr-4 py-2 text-silver focus:outline-none focus:border-silver transition-colors"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Filter size={20} className="text-gray-400" />
                    <select
                        className="bg-gray-900 border border-gray-800 rounded-lg px-4 py-2 text-silver focus:outline-none focus:border-silver transition-colors"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="cancelled">Cancelled</option>
                    </select>
                </div>
            </div>

            <div className="glass-morphism rounded-xl overflow-hidden">
                {loading ? (
                    <div className="py-20 text-center">
                        <div className="w-12 h-12 border-4 border-gray-600 border-t-silver rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-gray-400">Loading appointments...</p>
                    </div>
                ) : filteredSchedules.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b border-gray-800 bg-white/5">
                                    <th className="text-left py-4 px-6 font-semibold text-gray-300">Client Details</th>
                                    <th className="text-left py-4 px-6 font-semibold text-gray-300">Meeting Time</th>
                                    <th className="text-left py-4 px-6 font-semibold text-gray-300">Status</th>
                                    <th className="text-right py-4 px-6 font-semibold text-gray-300">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-800">
                                {filteredSchedules.map((schedule) => (
                                    <tr key={schedule.id} className="hover:bg-white/5 transition-colors">
                                        <td className="py-4 px-6 text-silver">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-silver to-gray-500 flex items-center justify-center font-bold text-black">
                                                    {schedule.name[0]}
                                                </div>
                                                <div>
                                                    <div className="font-semibold">{schedule.name}</div>
                                                    <div className="text-sm text-gray-400 flex items-center gap-1">
                                                        <Mail size={12} /> {schedule.email}
                                                    </div>
                                                    {schedule.company && (
                                                        <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                                            <Building size={10} /> {schedule.company}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-4 px-6 text-silver">
                                            <div className="flex flex-col uppercase tracking-tighter">
                                                <span className="flex items-center gap-2 font-mono text-lg">
                                                    <Calendar size={14} className="text-gray-400" />
                                                    {format(new Date(schedule.scheduledDate), 'MMM dd, yyyy')}
                                                </span>
                                                <span className="flex items-center gap-2 text-sm text-gray-400">
                                                    <Clock size={14} />
                                                    {format(new Date(schedule.scheduledDate), 'hh:mm a')}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="py-4 px-6">
                                            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${getStatusBadge(schedule.status)}`}>
                                                {schedule.status}
                                            </span>
                                        </td>
                                        <td className="py-4 px-6 text-right">
                                            <div className="flex justify-end gap-2">
                                                {schedule.status === 'pending' && (
                                                    <button
                                                        onClick={() => handleUpdateStatus(schedule.id, 'confirmed')}
                                                        className="p-2 hover:bg-green-500/20 text-green-500 rounded-lg transition-colors"
                                                        title="Confirm Appointment"
                                                    >
                                                        <CheckCircle size={18} />
                                                    </button>
                                                )}
                                                {schedule.status !== 'cancelled' && (
                                                    <button
                                                        onClick={() => handleUpdateStatus(schedule.id, 'cancelled')}
                                                        className="p-2 hover:bg-red-500/20 text-red-500 rounded-lg transition-colors"
                                                        title="Cancel Appointment"
                                                    >
                                                        <XCircle size={18} />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleDelete(schedule.id)}
                                                    className="p-2 hover:bg-gray-700 text-gray-400 rounded-lg transition-colors"
                                                    title="Delete Record"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="py-20 text-center">
                        <Calendar size={48} className="mx-auto text-gray-700 mb-4" />
                        <h3 className="text-xl font-semibold text-silver">No appointments found</h3>
                        <p className="text-gray-400">Scheduled sessions will appear here.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ScheduleManagement;
